import type { VersionedDraftKit } from '@/lib/versioned-draft';
import type {
  ModelParams,
  ModelSettingsContent,
  ModelSettingsDraftContent,
  VersionedDetail,
} from '@matter_extractor/shared';
import { MODEL_SETTINGS_NAME, modelSettingsContentSchema } from '@matter_extractor/shared';

/**
 * What Réglages > Modèles holds while somebody moves a seat (vitrine.md section
 * 5, issue #192).
 *
 * The comparison that greys « Enregistrer », the version numbering and the
 * refusals of the pattern are the layer's (`versioned-draft.ts`). What is here
 * is the SHAPE of the models' draft and the functions that cross between it and
 * the wire - `VersionedDraftKit`.
 *
 * ## The draft IS the draft content
 *
 * The form already held a `ModelParams`; the draft is the same params under the
 * fixed name, where a seat may still be typed - an empty model id is a legal
 * draft and a refused version (`modelSettingsDraftContentSchema`). The editor
 * holds the draft content as it is, and a version is what
 * `modelSettingsContentSchema` accepts of it: the schema is asked rather than
 * re-decided, so the button and the server refuse the same thing. What the
 * server adds on top - the coherence of a seat with its model, a negative
 * tariff - comes back as a typed refusal the screen words from its code
 * (`modelRefusals`).
 */

export type ModelSettingsDraft = ModelSettingsDraftContent;

/** A draft around params the form moved: the name never moves. */
export function draftOf(params: ModelParams): ModelSettingsDraft {
  return { name: MODEL_SETTINGS_NAME, params };
}

/**
 * What the tab opens on: the server draft when there is one, the `développement`
 * version otherwise.
 *
 * The singleton is born at boot, so there is always a version to open on; a
 * `null` detail is a state the screen never reaches, and answering it with an
 * invented set of seats would be the one lie this screen must not tell.
 */
export function openingDraft(
  detail: VersionedDetail<ModelSettingsContent, ModelSettingsDraftContent> | null,
): ModelSettingsDraft {
  const opened = detail?.draft?.content ?? detail?.developmentContent ?? null;
  if (opened === null)
    throw new Error('Les modèles sont créés au démarrage : aucune version à ouvrir.');
  return opened;
}

/**
 * The version a draft would become, or `null` while a seat has no model id or a
 * shape the schema refuses - the shared schema decides, so the button and the
 * server refuse the same thing.
 */
export function normalizedContent(draft: ModelSettingsDraft): ModelSettingsContent | null {
  const parsed = modelSettingsContentSchema.safeParse(draft);
  return parsed.success ? parsed.data : null;
}

export const MODEL_SETTINGS_DRAFT_KIT: VersionedDraftKit<
  ModelSettingsContent,
  ModelSettingsDraftContent,
  ModelSettingsDraft
> = {
  opening: openingDraft,
  fromContent: (content) => content,
  normalized: normalizedContent,
  payload: (draft) => draft,
};
